import { Observable } from '@logz-pkg/observable';
import type { ChatMode } from '@logz-pkg/models';
import { LoggerService } from '../../core/logger/logger.service';
import { sessionStateService } from '../../state/session.state.service';
import { chatModeService, DEFAULT_CHAT_MODE } from './chat-mode';

// The Fast/Advanced composer-mode state machine (ORIONIQ-1231), shared by every chat shell (ORIONIQ-1538 B1a).
// Every guard below exists because of a shipped bug — the per-account latch, the seed generation and the
// asymmetric rollback are each a fix, so a second copy would re-introduce them one at a time.

class ChatModeStateService {
  // The ACTIVE conversation's Fast/Advanced composer mode (ORIONIQ-1231). Re-seeded whenever the active chat
  // changes (a new chat → the last-used mode; a loaded chat → its newest run's mode), NOT a single global
  // preference — so switching mode in one chat no longer changes every other conversation.
  readonly chatMode = new Observable<ChatMode>(DEFAULT_CHAT_MODE);
  // The mode a NEW chat starts in = the one the user LAST TOGGLED to (ORIONIQ-1231). Persisted by
  // `chatModeService` in orioniq-be's user-preferences document (`GET|PUT /orioniq-be/user/preferences`) — NOT app-api's
  // app-state KV, which it replaced in ORIONIQ-1475 so the choice follows the person onto mobile too.
  readonly lastUsedMode = new Observable<ChatMode>(DEFAULT_CHAT_MODE);

  // Load the persisted mode once per session. The latch is keyed by ACCOUNT + USER because the store is: one row
  // per (accountId, userId). Keying it by account alone let a second user logging into the same account in the
  // same tab inherit the first one's default and skip the read entirely.
  // Best-effort — a failed fetch keeps the default and clears the latch so a later seed retries.
  loadLastUsedMode = async (): Promise<void> => {
    const session = sessionStateService.session.get();
    const owner = `${session?.loggedInAccount?.id}:${session?.loggedInUser?.id}`;

    if (this.lastUsedLoadedFor === owner) return;

    this.lastUsedLoadedFor = owner;

    try {
      this.lastUsedMode.set(await chatModeService.get());
    } catch {
      this.lastUsedLoadedFor = undefined;
    }
  };

  // Seed the ACTIVE conversation's composer mode (ORIONIQ-1231): a loaded chat resumes its newest run's mode, a
  // new/empty chat starts in the last-used one. A known loaded mode is applied SYNCHRONOUSLY — no await on the
  // account-default fetch (which it doesn't need), so the composer never lags on the prior conversation's mode
  // after a switch (bugbot: composer mode lags). Only a new/empty chat awaits the stored value.
  seedChatMode = async (loadedMode?: ChatMode): Promise<void> => {
    // Bump first so this call supersedes any in-flight seed (incl. a slow new-chat one awaiting below).
    this.seedGeneration += 1;

    const generation = this.seedGeneration;

    if (loadedMode) {
      this.chatMode.set(loadedMode);

      return;
    }

    await this.loadLastUsedMode();

    // A newer seed ran while we awaited (e.g. the user loaded a conversation, which set its mode synchronously) —
    // don't clobber it with this stale new-chat default (bugbot: stale async seed overwrites mode).
    if (generation !== this.seedGeneration) return;

    // Observable.get() is typed `ChatMode | undefined`; the observable is seeded with DEFAULT_CHAT_MODE so this
    // fallback is unreachable at runtime, but it satisfies chatMode.set(ChatMode).
    this.chatMode.set(this.lastUsedMode.get() ?? DEFAULT_CHAT_MODE);
  };

  // A user toggle: apply to the active conversation AND persist it as the mode the next new chat starts in.
  // ORIONIQ-1231 — any toggle, in any chat, updates the default for the next new chat. `onPersistFailed` is the
  // shell's user-facing notice: the toast component is app-specific, so the shell supplies it.
  setChatMode = (mode: ChatMode, onPersistFailed?: () => void): void => {
    const previous = this.lastUsedMode.get() ?? DEFAULT_CHAT_MODE;

    this.chatMode.set(mode);
    this.lastUsedMode.set(mode);

    // The write is deliberately not awaited — the toggle is optimistic, and blocking the picker on a round
    // trip would make it feel broken. But it must not be DROPPED: `void promise` attaches no handler, so a
    // failed PUT used to surface as an unhandled rejection and nothing else. The user was left believing
    // their default had changed, and the next session read the unwritten value and silently reverted it.
    //
    // Only `lastUsedMode` is rolled back. `chatMode` is the ACTIVE conversation's mode and the turn that just
    // went out carried it, so reverting it would misreport what the agent was actually asked.
    chatModeService.set(mode).catch(error => {
      this.lastUsedMode.set(previous);
      // Re-read on the next seed rather than trusting the rolled-back value, which is only a guess at what
      // the server still holds.
      this.lastUsedLoadedFor = undefined;
      LoggerService.logError({ message: 'OrionIQ: failed to persist the chat mode', error, isFailedRequest: true });
      onPersistFailed?.();
    });
  };

  /**
   * Seed the composer once per SESSION — for a host with no conversation identity to key on (Classic seeds on the
   * drawer's first open; app-ui keys its own seed on sessionId and does not use this).
   *
   * The latch lives HERE rather than in the consumer so `clear()` resets it together with the values and the load
   * latch. A latch in the consumer is unreachable from `clear()`, which left the composer pinned to the default
   * for the rest of the tab after an SPA account switch.
   *
   * Latched on SUCCESS only: a failed read leaves `lastUsedLoadedFor` clear, so the next open retries.
   */
  seedOnce = async (): Promise<void> => {
    if (this.seededOnce) return;

    await this.seedChatMode();

    this.seededOnce = this.lastUsedLoadedFor !== undefined;
  };

  // Drop every per-user value so a logout or an SPA account switch cannot carry one user's mode into the next
  // session in this tab. Resets the seed latch too, so the next drawer open re-seeds from the NEW owner.
  clear = (): void => {
    this.seedGeneration += 1;
    this.chatMode.set(DEFAULT_CHAT_MODE);
    this.lastUsedMode.set(DEFAULT_CHAT_MODE);
    this.lastUsedLoadedFor = undefined;
    this.seededOnce = false;
  };

  private seededOnce = false;
  // The `accountId:userId` the value was loaded for. Any change — an SPA account switch, or a different user in
  // the same tab — forces a refetch, so the latch cannot serve someone else's default (bugbot #4).
  private lastUsedLoadedFor: string | undefined = undefined;

  // Supersedes an in-flight seed: every seedChatMode call bumps this, and a slow new-chat seed only commits the
  // stored value if it's still the latest (bugbot: stale async seed overwrites mode).
  private seedGeneration = 0;
}

export const chatModeStateService = new ChatModeStateService();
